<template>
  <div class="wrapper" v-bind="$attrs">
    <editor v-model="tinymceHtml" :init="initConfig"></editor>
  </div>
  <ehf-image-upload editorMode v-model:visible="imageSelect" @change="insertImage" limit="5mb" />
  <ehf-video-upload
    editorMode
    v-model:visible="videoSelect"
    @change="insertVideo"
    :max="1"
    :limit="initConfig.video_limit"
    :limitTime="initConfig.limit_video_time"
  />
  <pdf-to-image v-model:show="pdfModalShow" @success="insertContent"></pdf-to-image>
  <upload-word v-model:show="wordModalShow" @success="setContent"></upload-word>
</template>

<script>
  export default {
    name: 'EhfEditor',
  }
</script>

<script setup>
  import { ref, toRefs, onMounted, watch, nextTick } from 'vue'
  import Editor from '@tinymce/tinymce-vue'
  import tinymce from 'tinymce/tinymce'
  import { message } from 'ant-design-vue'
  import { nanoid } from 'nanoid'
  import { uploadUrlImg as uploadUrlImgAPI, doHtmlSymbolReplace, dataURLtoFile, compressImage, getImgType } from '@/utils/index.js'
  import { transformImgHtmlByWebpSupport, getImgUrlByWebpSupport } from '@/utils/webp_utils'
  import { checkSelfDomain, handleImgUpload } from './static/utils'
  import { catchImage } from '@/api/common'
  import { uploadImg } from '@/api/system/imageGroup'
  import PdfToImage from './components/PdfToImage.vue'
  import UploadWord from './components/UploadWord.vue'

  // 引入插件等编辑器相关文件
  import './static/plugins'

  const props = defineProps({
    html: {
      type: String,
      default: '',
    },
    config: {
      type: Object,
      default() {
        return {}
      },
    },
    dark: {
      type: Boolean,
      default: false,
    },
    autoResize: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    replaceSymbol: {
      type: Boolean,
      default: false,
    },
    notImgAutoWidth: {
      type: Boolean,
      default: false,
    },
    catalogStructure: {
      type: String,
      default: '',
    },
    bookElectronization: {
      type: Boolean,
      default: false,
    },
    isStory: {
      type: Boolean,
      default: false,
    },
    isScoreKaoxun: {
      type: Boolean,
      default: false,
    },
  })
  const emit = defineEmits(['update:html', 'change', 'inited', 'focus', 'keyUpEnter', 'refreshAuto', 'compositionend', 'blur', 'click', 'compositionstart'])

  const { html } = toRefs(props)
  const tinymceHtml = ref('') // 编辑器中使用的html代码
  const imageSelect = ref(false) // 是否显示图片选择组件
  const videoSelect = ref(false) // 视频组件控制
  const editor135 = ref(null) // 135编辑器网页实例
  const pdfModalShow = ref(false) // pdf组件控制
  const wordModalShow = ref(false) // word组件控制
  const tinyEditorEl = ref(null) // 编辑器实例
  const isInputting = ref(false) // 当前是否为输入法唤起状态
  const afterChangeHtml = ref('') // 变动后的文本
  const isPaste = ref(false) //触发了粘贴

  // 图片转换webp
  const transformImgToWebp = (str, isUrl) => {
    // 开启配置才进行转换
    if (props.config.imageWebp) {
      if (isUrl) {
        // 如果是图片链接
        return getImgUrlByWebpSupport(str)
      } else {
        return transformImgHtmlByWebpSupport(str, null, null, true)
      }
    } else {
      return str
    }
  }

  tinymceHtml.value = transformImgToWebp(html.value)
  watch(html, (val) => {
    if (!tinymceHtml.value || val !== tinymceHtml.value.replace(/\r|\n|\r\n/g, '')) {
      tinymceHtml.value = transformImgToWebp(val)
    }
  })
  watch(tinymceHtml, (val, old_val) => {
    var table_reg = /<\/table>/g
    if (table_reg.test(old_val) && !table_reg.test(val)) {
      // 判断是否移除了table，如果移除了table，同时移除table插件中用于拖动调整table的内容，避免其余内容无法正常显示
      tinyEditorEl.value.$('html').find('.ephox-snooker-resizer-bar').remove()
    }
    afterChangeHtml.value = val.replace(/\r|\n|\r\n/g, '')
    contentChangeEmit()
  })

  const contentChangeEmit = () => {
    // 当输入法唤起时的变动不是变动，此时还只是拼音
    if (!isInputting.value) {
      emit('update:html', afterChangeHtml.value)
      emit('change', afterChangeHtml.value)
    }
  }

  onMounted(() => {
    tinymce.init({})
  })
  // 默认配置
  const initConfig = {
    language_url: '/tinymce/zh_CN.js',
    language: 'zh_CN',
    skin_url: '/tinymce/skins/ui/oxide',
    icons_url: '/icons/custom/icons.js?v=20220419', // load icon pack
    icons: 'custom',
    height: 400,
    fontsize_formats:
      '8px 9px 10px 11px 12px 13px 14px 15px 16px 17px 18px 19px 20px 21px 22px 23px 24px 25px 26px 27px 28px 29px 30px 31px 32px 33px 34px 35px 36px',
    font_formats:
      '宋体=宋体,SimSun;微软雅黑=微软雅黑,"Microsoft YaHei";楷体=楷体,楷体_GB2312,SimKai;黑体=黑体,SimHei;隶书=隶书,SimLi;Andale Mono=andale mono,times;Arial=arial,helvetica,sans-serif;Arial Black=arial black,avant garde;Book Antiqua=book antiqua,palatino;Comic Sans MS=comic sans ms,sans-serif;Courier New=courier new,courier;Georgia=georgia,palatino;Helvetica=helvetica;Impact=impact,chicago;Tahoma=tahoma,arial,helvetica,sans-serif;Terminal=terminal,monaco;Times New Roman=times new roman,times;Trebuchet MS=trebuchet ms,geneva;Verdana=verdana,geneva;Webdings=webdings;Wingdings=wingdings,zapf dingbats;Courier New=courier new,courier,monospace;AkrutiKndPadmini=Akpdmi-n ',
    plugins:
      'paste print preview searchreplace autolink directionality visualblocks visualchars fullscreen image link media template code codesample table charmap hr pagebreak nonbreaking anchor insertdatetime advlist lists wordcount textpattern ax_wordlimit toolbarsticky formatpainter',
    menubar: false, // 菜单栏
    branding: false, // 底部支持信息
    toolbar_mode: 'wrap', // 工具栏模式
    toolbar: [
      'undo redo | formatselect | fontselect | fontsizeselect | bold italic underline strikethrough forecolor lineheight | alignleft aligncenter alignright alignjustify | formatpainter removeformat link imageSelect imageAutosize media | fullscreen code  preview',
    ],
    ax_wordlimit_num: false, // 字数限制
    ax_wordlimit_callback: function (editor, txt, num) {
      message.warning('当前' + (props.config.ax_wordlimit_type === 'all' ? '字符' : '字数') + '：' + txt.length + '，最多可输入：' + num)
    },
    entity_encoding: 'raw', // 是否进行实体转换
    link_title: false, // link插件 对话框中“标题”的开关
    placeholder: props.placeholder,
    setup: (editor) => {
      if (props.config.readonly === true) {
        editor.setMode('readonly')
      }
      if (typeof props.config.scrollCallback === 'function') {
        editor.on('ScrollContent', (e) => {
          props.config.scrollCallback(e)
        })
      }
      // 监听输入法唤起/关闭
      editor.on('compositionstart', () => {
        compositionstartListener()
      })
      editor.on('compositionend', () => {
        compositionendListener()
      })
      editor.on('blur', () => {
        blurListener()
      })
      editor.on('click', () => {
        clickListener()
      })
      // 初始化前的处理
      editor.ui.registry.addButton('imageSelect', {
        icon: 'image',
        tooltip: '图片组件',
        onAction: () => {
          // 视频与图片只能上传其一
          if (initConfig.only_image_or_video && html.value && html.value.indexOf('<video') != -1) {
            message.warning('无法同时上传视频、图片')
            return
          }
          imageSelect.value = true
        },
      })
      editor.ui.registry.addToggleButton('imageAutosize', {
        icon: 'imageAutosize',
        tooltip: '图片宽度自适应',
        disabled: true,
        onAction: (api) => {
          const selectedNode = editor.selection.getNode()
          if (!api.isActive()) {
            selectedNode.dataset.oriWidth = selectedNode.width
            selectedNode.style.width = '100%'
            selectedNode.style.height = 'auto'
            api.setActive(true)
          } else {
            if (!selectedNode.dataset.oriWidth) {
              selectedNode.style.width = 'auto'
            } else {
              selectedNode.style.width = selectedNode.dataset.oriWidth + 'px'
            }
            api.setActive(false)
          }
        },
        onSetup: (api) => {
          const selectionEventCb = (event) => {
            api.setDisabled(event.element.nodeName.toLowerCase() !== 'img')
            api.setActive(event.element.nodeName.toLowerCase() === 'img' && event.element.style.width === '100%')
          }
          editor.on('NodeChange', selectionEventCb)

          return function () {
            editor.off('NodeChange', selectionEventCb)
          }
        },
      })
      // 上传视频按钮media 自定义, 弹出框外部导入
      editor.ui.registry.addButton('videoSelect', {
        icon: 'videoSelect',
        tooltip: '视频组件',
        onAction: () => {
          if (html.value) {
            // 视频与图片只能上传其一
            if (initConfig.only_image_or_video && html.value.indexOf('<img') != -1) {
              message.warning('无法同时上传视频、图片')
              return
            }
            // 视频只能上传一个
            if (initConfig.max_video_num && html.value.indexOf('<video') != -1) {
              message.warning('只能上传一个视频')
              return
            }
          }
          videoSelect.value = true
        },
      })
      editor.ui.registry.addButton('135editor', {
        icon: '135editor',
        tooltip: '135编辑器',
        onAction: () => {
          editor.focus()
          editor135.value = window.open('https://www.135editor.com/simple_editor.html?callback=true&appkey=')
          window.removeEventListener('message', onContentFrom135)
          window.addEventListener('message', onContentFrom135, false)
        },
      })
      editor.ui.registry.addButton('pdfSelect', {
        icon: 'pdfSelect',
        tooltip: 'PDF组件',
        onAction: () => {
          pdfModalShow.value = true
        },
      })
      editor.ui.registry.addButton('wordFormat', {
        icon: 'wordFormat',
        tooltip: '格式化DOC文档代码',
        onAction: () => {
          if (tinymceHtml.value) {
            let html = tinymceHtml.value
            html = html.replace(/&#39;/, "'")
            html = html.replace(/<(div|p|span|ol|ul|li|td|h[1-6])(.*?)\s*style="[^"]*"(.*?)>/gi, '<$1$2$3>')
            html = html.replace(/font-family:.*?(;|")/gi, '$1')
            html = html.replace(/font-size:.*?(;|")/gi, '$1')
            html = html.replace(/<span\s*style="[^"]*?">(&nbsp;|\s)*?<\/span>/gi, '')
            html = html.replace(/<span\s*style="[^"]*?"><br\/><\/span>/gi, '<br/>')
            html = html.replace(/<span\s*style="[; ]*">(.*?)<\/span>/gi, '$1')
            html = html.replace(/<span>(&nbsp;|\s)*?<\/span>/gi, '')
            html = html.replace(/<p><\/p>/gi, '')
            html = html.replace(/<table(.*?)>/gi, '<table$1 border="1" cellspacing="0" cellpadding="0" width="100%">')
            tinymceHtml.value = html
          }
        },
      })
      editor.ui.registry.addButton('wordSelect', {
        icon: 'wordSelect',
        tooltip: 'Word组件',
        onAction: () => {
          wordModalShow.value = true
        },
      })
      //设置上标快捷键
      editor.shortcuts.add('ctrl+alt+s', 'Superscript', function () {
        editor.execCommand('superscript')
      })
      //设置下标快捷键
      editor.shortcuts.add('ctrl+alt+x', 'Subscript', function () {
        editor.execCommand('subscript')
      })
    },
    init_instance_callback: (editor) => {
      // 初始化完成的回调
      tinyEditorEl.value = editor
      if (initConfig.enablePasteImage || (initConfig.toolbar && initConfig.toolbar[0] && initConfig.toolbar[0].indexOf('imageSelect') > -1)) {
        editor.on('paste', () => {
          isPaste.value = true
          setTimeout(() => {
            catchImageUrl(tinymceHtml.value)
          }, 100)
        })
        editor.on('drop', () => {
          setTimeout(() => {
            catchImageUrl(tinymceHtml.value)
          }, 100)
        })
      }
      editor.on('focus', () => {
        tinymce.activeEditor.fire('change')
        emit('focus')
      })
      // 编辑器默认不响应tab键缩进，需要手动处理监听
      editor.on('keydown', function (evt) {
        if (evt.keyCode == 9) {
          if (evt.shiftKey) {
            editor.execCommand('Outdent')
          } else {
            editor.execCommand('Indent')
          }
          evt.preventDefault()
          evt.stopPropagation()
        }
      })
      //当有层级结构时，说明是书籍目录生成，才需要绑定keyup事件
      setTimeout(() => {
        //使用延时由于一开始获取不到catalogStructure
        if (props.catalogStructure > 0) {
          editor.on('keydown', (e) => {
            //浏览器有默认的F1打开帮助界面行为
            if (e.keyCode == '112') {
              e.preventDefault()
            }
            //输入状态不处理
            setTimeout(() => {
              if (isInputting.value) {
                return
              }
              if (e.keyCode != '113' && e.keyCode != '112') {
                //编辑器内容不会自动更新，需要手动触发
                tinymce.activeEditor.fire('change')
                nextTick(() => {
                  emit('keyUpEnter')
                })
              }
              if (e.keyCode == '113') {
                e.preventDefault()
                changeSymbol('delete')
              }
              if (e.keyCode == '112') {
                e.preventDefault()
                changeSymbol('add')
              }
            }, 100)
          })
        }
        if (props.bookElectronization) {
          editor.on('keydown', () => {
            emit('keyUpEnter')
          })
          editor.on('mousemove', () => {
            emit('keyUpEnter')
          })
          editor.on('wheel', () => {
            emit('keyUpEnter')
          })
        }
      }, 1000)

      emit('inited', editor)
    },
    paste_data_images: true,
    paste_enable_default_filters: false,
    paste_filter_drop: false,
    paste_remove_styles_if_webkit: false,
    paste_preprocess: function (plugin, args) {
      let html = args.content
        .replace(/data-[^> ]+="[^>"]*"/gm, '')
        .replaceAll('<section', '<div')
        .replaceAll('</section>', '</div>')
        .replace(/(width:\s?\d*px;)/g, '$1max-width:100%;')
      if (props.isStory) {
        html = html
          .replace(/<video[^>]*>.*?<\/video>/gi, '') // 移除视频标签及其内容
          .replace(/<h[1-6](.*?)>/gi, '<h4$1>') // 将 h1~h6 标签替换为 h4
          .replace(/<\/h[1-6]>/gi, '</h4>') // 替换 h1~h6 的关闭标签为 </h4>
          .replace(/<(?!p|b|img|a|h4|div|span|strong)([a-zA-Z][^>]*)>/gi, () => {
            // 保留非白名单标签的内部内容
            return ''
          })
          .replace(/<\/(?!p|b|img|a|h4|div|span|strong)[a-zA-Z]+>/gi, '')
          .replace(/<([a-zA-Z]+)([^>]*)>/g, (match, tagName, attributes) => {
            const lowerTagName = tagName.toLowerCase()

            // 如果是 img 或 strong 标签，直接返回原始内容
            if (lowerTagName === 'img' || lowerTagName === 'strong') {
              return match
            }

            // 对其他标签移除style 属性
            const cleanedAttributes = attributes.replace(/style="[^"]*"/gi, '') // 移除 style 属性

            return `<${tagName}${cleanedAttributes}>`
          })
      } else if (props.isScoreKaoxun) {
        html = html
          .replace(/<video[^>]*>.*?<\/video>/gi, '') // 移除视频标签及其内容
          .replace(/<h[1-6](.*?)>/gi, '<h4$1>') // 将 h1~h6 标签替换为 h4
          .replace(/<\/h[1-6]>/gi, '</h4>') // 替换 h1~h6 的关闭标签为 </h4>
          .replace(/<(?!p|b|img|a|h4|div|span|strong)([a-zA-Z][^>]*)>/gi, () => {
            // 保留非白名单标签的内部内容
            return ''
          })
          .replace(/<\/(?!p|b|img|a|h4|div|span|strong)[a-zA-Z]+>/gi, '')
          .replace(/<([a-zA-Z]+)([^>]*)>/g, (match, tagName, attributes) => {
            const lowerTagName = tagName.toLowerCase()

            // 如果是 img 或 strong 标签，直接返回原始内容
            if (lowerTagName === 'img' || lowerTagName === 'strong') {
              return match
            }

            // 对其他标签移除style 属性
            console.log('attributes', attributes)
            const cleanedAttributes = attributes.replace(/style="[^"]*"/gi, (styles) => {
              // 正则表达式匹配 color 和 text-align 属性
              const colorRegex = /color:\s*([^;]+);?/i
              const textAlignRegex = /text-align:\s*([^;]+);?/i

              // 提取 color 和 text-align 属性值
              const colorMatch = styles.match(colorRegex)
              const textAlignMatch = styles.match(textAlignRegex)

              // 构建新的 style 属性值
              let newStyles = ''
              if (colorMatch) {
                newStyles += `color: ${colorMatch[1]}; `
              }
              if (textAlignMatch) {
                newStyles += `text-align: ${textAlignMatch[1]};`
              }

              // 返回新的 style 属性，如果 newStyles 为空，则不包含 style 属性
              return newStyles ? `style="${newStyles.trim()}"` : ''
            })
            console.log('cleanedAttributes', cleanedAttributes)

            return `<${tagName}${cleanedAttributes}>`
          })
      }
      if (props.replaceSymbol) {
        html = doHtmlSymbolReplace(html)
      }
      if (props.catalogStructure > 0) {
        const catalogDiv = document.createElement('div')
        catalogDiv.innerHTML = html
        if (catalogDiv.querySelectorAll('h3, h4, h5').length == 1) {
          html = catalogDiv.innerText
        }
      }
      // 路由链接防污染处理start
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      const aNodeList = tempDiv.querySelectorAll('a[href]')
      for (let index = 0; index < aNodeList.length; index++) {
        const hrefArr = aNodeList[index].getAttribute('href').split('#/')
        if (hrefArr.length > 1 && checkSelfDomain(hrefArr[0])) {
          aNodeList[index].setAttribute('href', '#/' + hrefArr[1])
        }
      }
      // 路由链接防污染处理end
      const imgList = tempDiv.querySelectorAll('img')
      for (let index = 0; index < imgList.length; index++) {
        imgList[index].style.display = 'inline-block'
        if (imgList[index].src && imgList[index].src.indexOf('blob:') == 0) {
          // 暂时隐藏粘贴图片，防止闪动
          imgList[index].style.visibility = 'hidden'
          imgList[index].setAttribute('data-need-show', 1)
          imgList[index].setAttribute('data-need-paste-upload', 1)
        }
      }
      const videoList = tempDiv.querySelectorAll('video')
      for (let index = 0; index < videoList.length; index++) {
        videoList[index].style.width = '100%'
        videoList[index].style.height = 'auto'
        videoList[index].style.display = 'inline-block'
        videoList[index].style.margin = '0'
      }

      // 复制的表格处理
      const tableList = tempDiv.querySelectorAll('table')
      for (var i = 0; i < tableList.length; i++) {
        // 默认宽度设置为100%
        tableList[i].style.width = '100%'
      }

      html = tempDiv.innerHTML
      args.content = html
    },
    // 自定义视频插入代码
    video_template_callback: function (data) {
      return (
        '<video style="display: inline-block;width:100%;height:auto;margin: 0;" width="100%" height="auto"' +
        (data.poster ? ' poster="' + data.poster + '"' : '') +
        ' controls="controls">\n' +
        '<source src="' +
        data.source +
        '"' +
        (data.sourcemime ? ' type="' + data.sourcemime + '"' : '') +
        ' />\n' +
        (data.altsource ? '<source src="' + data.altsource + '"' + (data.altsourcemime ? ' type="' + data.altsourcemime + '"' : '') + ' />\n' : '') +
        '</video>'
      )
    },
    // 图片上传
    images_upload_handler: (blobInfo, success, failure) => {
      if (!initConfig.enablePasteImage && initConfig.toolbar && initConfig.toolbar[0] && initConfig.toolbar[0].indexOf('imageSelect') == -1) {
        handleImgUpload(blobInfo, success, failure)
      }
    },
  }
  if (props.dark) {
    initConfig.skin_url += '-dark'
  }
  if (props.autoResize) {
    initConfig.plugins += ' autoresize'
    initConfig.toolbar_sticky = true
  }
  const unChangeableList = ['language_url', 'language', 'skin_url', 'plugins', 'setup'] // 不允许通过传入config修改的配置项
  // eslint-disable-next-line vue/no-setup-props-destructure
  const config = props.config
  if (config.inline && config.min_height > 0) {
    // 行内模式不支持min_height，使用样式覆盖实现
    config.content_style = config.content_style || ''
    config.content_style += ` .mce-content-body{min-height:${config.min_height}px;}`
  }
  for (let key in config) {
    if (unChangeableList.indexOf(key) === -1) {
      initConfig[key] = config[key]
    }
  }

  // 唤起输入法
  const compositionstartListener = () => {
    isInputting.value = true
    emit('compositionstart')
  }

  // 关闭输入法
  const compositionendListener = () => {
    isInputting.value = false
    contentChangeEmit()
    emit('compositionend')
  }

  //编辑器失去焦点
  const blurListener = () => {
    emit('blur')
  }

  const clickListener = () => {
    emit('click')
  }

  // 粘贴图片复原处理
  const fixPasteImage = () => {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = tinymceHtml.value
    const imgList = tempDiv.querySelectorAll('img[data-need-show="1"]')
    if (imgList.length > 0) {
      for (let index = 0; index < imgList.length; index++) {
        imgList[index].style.visibility = ''
        imgList[index].removeAttribute('data-need-show')
      }
      tinymceHtml.value = tempDiv.innerHTML
    }
  }
  // 粘贴图片上传后处理
  const handlePasteImageAfterUpload = (targetImgUrl) => {
    if (!targetImgUrl || !props.config.imgInitialWidth) {
      return
    }
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = tinymceHtml.value
    const imgList = tempDiv.querySelectorAll('img[data-need-paste-upload="1"]')
    if (imgList.length > 0) {
      for (let index = 0; index < imgList.length; index++) {
        if (imgList[index].src === targetImgUrl) {
          // 针对指定图片增加宽度限制
          imgList[index].style.width = `${props.config.imgInitialWidth}px`
          imgList[index].removeAttribute('data-need-paste-upload')
        }
      }
      tinymceHtml.value = tempDiv.innerHTML
    }
  }
  // 获取粘贴图片原始链接列表
  const getImageOriginList = () => {
    const imgReg_g = /<img.*?>/g
    const imgElList = tinymceHtml.value.match(imgReg_g)
    const bgUrlReg_g = /(:|\s*)url\s*\(\s*('|")?([^\s'"]*?)\2?\)/gi
    const bgUrlList = tinymceHtml.value.match(bgUrlReg_g)
    let imgUrlList = []
    let base64ImgList = []
    const notList = ['127.0.0.1', 'localhost', 'imgcdn.ehafo.com', 'cdn.ehafo.com', 'kanban.ehafo.com']
    if (imgElList && imgElList.length > 0) {
      const urlReg = /\ssrc=['"](.*?)['"]/
      imgElList.forEach((el) => {
        const url = el.match(urlReg)[1]
        let shouldUpload = true
        notList.some((str) => {
          if (shouldUpload) {
            shouldUpload = url.indexOf(str) === -1
          } else {
            return true
          }
        })
        if (shouldUpload) {
          if (url.indexOf('data:image') > -1) {
            // base64图片处理
            const imgItem = {
              url,
              loading_url: 'https://kanban.ehafo.com/common/loading.gif' + '?id=' + nanoid(),
              error_url: 'https://kanban.ehafo.com/common/img_error.svg',
            }
            tinymceHtml.value = tinymceHtml.value.replaceAll(url, imgItem.loading_url)
            base64ImgList.push(imgItem)
          } else if (imgUrlList.indexOf(url) === -1) {
            imgUrlList.push(url)
          }
        }
      })
    }
    if (bgUrlList && bgUrlList.length > 0) {
      const bgUrlReg = /(:|\s*)url\s*\(\s*('|")?([^\s'"]*?)\2?\)/i
      bgUrlList.forEach((el) => {
        const url = el.match(bgUrlReg)[3]
        let shouldUpload = true
        notList.some((str) => {
          if (shouldUpload) {
            shouldUpload = url.indexOf(str) === -1
          } else {
            return true
          }
        })
        if (shouldUpload) {
          if (imgUrlList.indexOf(url) === -1) {
            imgUrlList.push(url)
          }
        }
      })
    }
    return {
      imgUrlList,
      base64ImgList,
    }
  }
  // 抓取内容中的图片链接，并将外链转存至cdn中
  const catchImageUrl = () => {
    fixPasteImage()
    const { imgUrlList, base64ImgList } = getImageOriginList()
    if (base64ImgList.length > 0) {
      let base64ImgPromiseArr = []
      base64ImgList.forEach((item, index) => {
        base64ImgPromiseArr.push(
          new Promise((resolve) => {
            let extName = getImgType(item.url)
            let file = dataURLtoFile(item.url, `tinymce_paste_image_${new Date().getTime()}_${index}.${extName}`)
            compressImage(file, 0.7, 2 * 1024 * 1024).then((resFile) => {
              const body = {
                file: resFile,
                group_id: '',
                upload: true,
              }
              uploadImg(body)
                .then((ret) => {
                  if (ret.code === 0) {
                    handlePasteImageAfterUpload(item.loading_url)
                    tinymceHtml.value = tinymceHtml.value.replaceAll(item.loading_url, transformImgToWebp(ret.data, true))
                    resolve(true)
                  } else {
                    tinymceHtml.value = tinymceHtml.value.replaceAll(item.loading_url, item.error_url)
                    resolve(false)
                  }
                })
                .catch(() => {
                  tinymceHtml.value = tinymceHtml.value.replaceAll(item.loading_url, item.error_url)
                  resolve(false)
                })
            })
          })
        )
      })
      Promise.all(base64ImgPromiseArr).then((arr) => {
        const failedArr = arr.filter((state) => {
          return state != true
        })
        if (failedArr.length > 0) {
          message.error(`${failedArr.length}张图片上传失败，单张图片大小不能超过2M`)
        }
      })
    }
    if (imgUrlList.length > 0) {
      catchImage({ source: imgUrlList }).then((res) => {
        if (res.state === 'SUCCESS') {
          const list = res.list
          const imgDomain = '//imgcdn.ehafo.com'
          list.forEach((item) => {
            tinymceHtml.value = tinymceHtml.value.replaceAll(item.source, transformImgToWebp(imgDomain + item.url, true))
          })
          // content.emit('catchImg', list)
        }
      })
    }
  }
  // 处理135编辑器中返回的内容
  const onContentFrom135 = (event) => {
    if (typeof event.data !== 'string') {
      if (event.data.ready) {
        editor135.value.postMessage(tinymceHtml.value, '*')
      }
      return
    }
    if (event.data.indexOf('<') !== 0) return
    tinymceHtml.value = event.data
    catchImageUrl(tinymceHtml.value)
    window.removeEventListener('message', onContentFrom135)
  }

  // 在光标位置插入内容
  const insertContent = (content) => {
    tinymce.activeEditor.insertContent(content)
  }
  const setContent = (content) => {
    console.log('content', content)
    tinymceHtml.value = content
    // tinymce.activeEditor.setContent(content)
  }
  //获取最新的内容
  const getContent = () => {
    return tinymce.activeEditor.save({ no_linebreaks: true })
  }
  //清除所有的撤销栈
  const clearUndo = () => {
    tinymce.activeEditor.undoManager.clear()
  }
  // 在光标位置插入图片
  const insertImage = (urlList) => {
    let elList = ''
    if (props.notImgAutoWidth) {
      elList = urlList
        .map((url) => {
          return `<img src="${url}" />`
        })
        .join('')
    } else {
      elList = urlList
        .map((url) => {
          return `<img src="${url}" style="width: ${props.config.imgInitialWidth > 0 ? props.config.imgInitialWidth + 'px' : '100%'};display: inline-block;" />`
        })
        .join('')
    }
    elList = transformImgToWebp(elList) // 图片转webp
    insertContent(elList)
  }
  // 在光标位置插入视频
  const insertVideo = async (urlArr) => {
    if (urlArr.length === 0) {
      message.error('富文本编辑器中的视频组件有问题, 联系管理员, 长度不应该为0')
      console.error('富文本编辑器中的视频组件有问题, 联系管理员, 长度不应该为0')
    }
    let str = ''
    let promiseArr = []
    let throwFlag = false
    urlArr.forEach((item) => {
      promiseArr.push(
        new Promise((resolve) => {
          let videoName = ''
          try {
            videoName = item.split('.com/')[1].split('?sign')[0]
          } catch (e) {
            console.log(e)
          }
          if (!videoName) {
            throwFlag = true
          }
          uploadUrlImgAPI(`${item}${item.includes('?') ? '&' : '?'}vframe/jpg/offset/0`, `poster_${videoName}_${new Date().getTime()}.jpg`).then((url) => {
            resolve(url + '!/fw/900/compress/true') //加入压缩参数防止过大
          })
        })
      )
    })
    if (throwFlag) {
      message.error('插入富文本编辑器的url是无效字符, 请联系管理员')
      console.error('插入富文本编辑器的url是无效字符, 请联系管理员')
      return
    }
    await Promise.all(promiseArr).then((posterArr) => {
      urlArr.forEach((item, index) => {
        str += `<video src="${item}" poster="${posterArr[index]}" width="100%" height="auto" class="ehafo-cdn-video" controls></video>`
      })
    })
    insertContent(str)
  }
  // 更新工具栏吸顶
  const updateToolbarSticky = () => {
    if (tinymce.activeEditor && typeof tinymce.activeEditor.updateToolbarSticky === 'function') {
      tinymce.activeEditor.updateToolbarSticky()
    }
  }
  // 聚焦激活的编辑器实例
  const triggerFocus = () => {
    if (tinymce.activeEditor) {
      tinymce.activeEditor.focus()
    }
  }
  //对选中内容增加/减少一个字号
  const changeSymbol = (type) => {
    let bm = getOffset()
    const selectContent = tinymce.activeEditor.selection.getSelectedBlocks()
    selectContent.forEach((item) => {
      if (type == 'add') {
        if (item.tagName == 'H4') {
          // 将标签更改为h3
          let newElement = document.createElement('h3')
          newElement.innerHTML = item.innerHTML
          item.parentNode.replaceChild(newElement, item)
        } else if (item.tagName == 'H5') {
          let newElement = document.createElement('h4')
          newElement.innerHTML = item.innerHTML
          item.parentNode.replaceChild(newElement, item)
        }
      } else if (type == 'delete') {
        if (item.tagName == 'H3') {
          // 将标签更改为h4
          let newElement = document.createElement('h4')
          newElement.innerHTML = item.innerHTML
          item.parentNode.replaceChild(newElement, item)
        } else if (item.tagName == 'H4' && props.catalogStructure == '2') {
          let newElement = document.createElement('h5')
          newElement.innerHTML = item.innerHTML
          item.parentNode.replaceChild(newElement, item)
        }
      }
    })
    //编辑器内容不会自动更新，需要手动触发
    tinymce.activeEditor.fire('change')
    nextTick(() => {
      setOffset(bm)
      //修改后发出重新编号
      emit('refreshAuto')
    })
  }
  //获取当前dom位于给定dom的字符数
  const countCharactersBeforeNode = (list) => {
    //当光标深度少于两级时，只是因为到了dom的末尾，最后的0被省略了，所以需要手动补上
    if (list.length == 2) {
      list.unshift(0)
    }
    let parser = new DOMParser()
    let dom_index = JSON.parse(JSON.stringify(list))
    //数组反转，原数组是dom有里到外的顺序，我们需要从外到里遍历
    dom_index.reverse()
    // 解析HTML字符串为DOM树
    //单纯的从tinymceHtml.value获取是拿不到dom刚被删除的时候的状态的，还是需要通过tinymce.activeEditor.getBody().innerHTML获取
    let doc = parser.parseFromString(tinymce.activeEditor.getBody().innerHTML, 'text/html')
    let nonTextNodes = doc.body.childNodes
    let doms = Array.from(nonTextNodes).filter((node) => node.nodeType !== Node.TEXT_NODE)
    // 用于存储字符总数的变量
    let totalChars = 0
    // 定义递归函数
    function traverse(node) {
      // 如果是文本节点，累加字符数
      if (node.nodeType === Node.TEXT_NODE) {
        totalChars += node.textContent.length
      } else if (node.nodeType === Node.ELEMENT_NODE && (node.tagName === 'BR' || node.tagName === 'IMG')) {
        //识别换行符并加1
        totalChars += 1
      }

      // 递归遍历子节点
      if (node.childNodes) {
        for (let i = 0; i < node.childNodes.length; i++) {
          traverse(node.childNodes[i])
        }
      }
    }
    //cur_index代表同级dom的索引值
    for (let i = 0; i < dom_index.length; i++) {
      let cur_index = dom_index[i]
      if (doms && doms.length > 0) {
        for (let j = 0; j < cur_index; j++) {
          traverse(doms[j])
        }
        //往深度获取，对应下一个dom的childNodes
        if (doms[cur_index]) {
          doms = doms[cur_index].childNodes
        }
      } else {
        //没有子级了，说明已经到了纯文本节点了，直接加上最后的偏移量
        totalChars += cur_index
      }
    }

    // 返回计算的字符总数
    return totalChars
  }
  //获取当前dom位于给定dom的字符数
  const getCharactersCurrentNode = (parents, num, is_next) => {
    // 用于存储字符总数的变量
    let totalChars = 0
    let is_paste = false
    let need_list = []

    // 定义递归函数
    function traverse(node) {
      //如果是is_next且差值为0,说明是在下一个dom的头部
      if (num != 0 && is_next && num - totalChars == 0 && node.textContent.length != 0 && !is_paste) {
        need_list = node.dom_index
        need_list.push('0')
        need_list.reverse()
        is_paste = true
        return
      }
      //判断当前的文本节点是否已经超过的预定的值，是的话可以结束循环了
      if (node.nodeType === Node.TEXT_NODE && !is_paste) {
        if ((is_next ? num + 1 : num) > totalChars + node.textContent.length) {
          totalChars += node.textContent.length
        } else {
          need_list = node.dom_index
          need_list.push(num - totalChars)
          need_list.reverse()
          is_paste = true
          return
        }
      }
      if (node.nodeType === Node.ELEMENT_NODE && (node.tagName === 'BR' || node.tagName === 'IMG')) {
        if ((is_next ? num + 1 : num) > totalChars + 1) {
          totalChars += 1
        } else {
          need_list = node.dom_index
          need_list.push(num - totalChars)
          need_list.reverse()
          is_paste = true
          return
        }
      }
      // 递归遍历子节点
      if (node.childNodes && !is_paste) {
        for (let i = 0; i < node.childNodes.length; i++) {
          if (node.dom_index) {
            node.childNodes[i].dom_index = JSON.parse(JSON.stringify(node.dom_index))
            node.childNodes[i].dom_index.push(i)
          } else {
            node.childNodes[i].dom_index = [i]
          }
          traverse(node.childNodes[i])
        }
      }
    }
    // 从文档的根节点开始遍历
    traverse(parents)

    // 返回计算的字符总数
    return need_list
  }
  // 获取光标位置并记录书签
  const getOffset = () => {
    let bm = tinymce.activeEditor.selection.getBookmark(2, true)
    let charCountBeforeNode = countCharactersBeforeNode(bm.start)
    bm.nums = charCountBeforeNode
    //up是指属于两个换行节点的分割线，但是这个时候光标要判断是在上一个节点的末尾还是下一个节点的开始，up为ture指的是需要在下一个dom头部
    //特殊情况：[1,0]结尾说明是上一个是[0,当前dom的末尾],不属于换行
    bm.up = bm.start[0] == 0 && bm.start[1] != 1
    //如果触发了粘贴，则说明一定在末尾
    if (isPaste.value) {
      bm.up = false
      isPaste.value = false
    }
    return bm
  }
  //根据书签设置光标位置
  const setOffset = (bm) => {
    let parser = new DOMParser()
    let doc = parser.parseFromString(tinymce.activeEditor.getBody().innerHTML, 'text/html')
    let need_list = getCharactersCurrentNode(doc.body, bm.nums, bm.up)
    bm.start = need_list
    tinymce.activeEditor.selection.moveToBookmark(bm)
  }

  defineExpose({
    updateToolbarSticky,
    triggerFocus,
    changeSymbol,
    getContent,
    clearUndo,
    insertContent,
    setContent,
    getOffset,
    setOffset,
  })
</script>

<style lang="scss" scoped>
  .wrapper {
    width: 100%;
  }
</style>
<style lang="scss">
  .mce-item-table table {
    width: 100% !important;
  }
</style>
